
import { useState, useEffect } from 'react';
import { useSupabaseClient } from './useSupabaseClient';
import { useToast } from './use-toast';
import { Procurement } from '@/types/procurement';

type ProcurementFilters = {
  statusFilter: string;
  typeFilter: string;
  searchQuery: string;
};

export const useProcurements = () => {
  const supabase = useSupabaseClient();
  const { toast } = useToast();
  const [procurements, setProcurements] = useState<Procurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ProcurementFilters>({
    statusFilter: "all",
    typeFilter: "all",
    searchQuery: ""
  });

  const fetchProcurements = async () => {
    try {
      setLoading(true);
      let query = supabase.from('procurements').select('*');
      
      // Apply filters if they are set
      if (filters.statusFilter !== "all") {
        query = query.eq('status', filters.statusFilter);
      }
      
      if (filters.typeFilter !== "all") {
        query = query.eq('procurement_type', filters.typeFilter);
      }
      
      if (filters.searchQuery) {
        query = query.ilike('title', `%${filters.searchQuery}%`);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setProcurements(data as Procurement[]);
    } catch (error: any) {
      console.error('Error fetching procurements:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte hämta upphandlingar: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteProcurement = async (id: string) => {
    try {
      const { error } = await supabase
        .from('procurements')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Update the local state by removing the deleted procurement
      setProcurements(procurements.filter(proc => proc.id !== id));
      
      toast({
        title: "Upphandling borttagen",
        description: "Upphandlingen har tagits bort",
      });
    } catch (error: any) {
      console.error('Error deleting procurement:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte ta bort upphandlingen: " + error.message,
        variant: "destructive",
      });
    }
  };

  const updateFilters = (newFilters: Partial<ProcurementFilters>) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  };

  useEffect(() => {
    fetchProcurements();
  }, [filters, supabase, toast]);

  return {
    procurements,
    loading,
    filters,
    updateFilters,
    deleteProcurement,
    refreshProcurements: fetchProcurements
  };
};
